import React, { useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import type { FormInstance } from 'antd/es/form';

import CustomInput from '@/components/CustomInput';
import CustomButton from '@/components/CustomButton';
import { useSpin } from '@/components/SpinContext';
import { AddNewRole, DeleteRole } from '@/apis/roles';
import { useToken } from '@/common/helper';
import { useUnAuthorize } from '@/hooks';
import type { ErrorLike } from '@/apis/types';
import { CustomTitle } from '@/components';
import { CustomDeleteModal } from '@/pages/Modals';
import './RolesAndPermissions.css';

type EditRoleData = {
  id?: string;
  name?: string;
  is_connected_with_anything?: boolean;
  is_delete?: boolean;
};

type AddNewRoleFormValues = {
  name: string;
};

type AddNewRoleFormProps = {
  form: FormInstance<AddNewRoleFormValues>;
  editData?: EditRoleData | null;
  onClose: () => void;
  fetchAllRoles: () => void;
};

type ApiCreateUpdateEnvelope = {
  data?: {
    is_success?: boolean;
    message?: string;
    data?: unknown;
  };
};

type ApiDeleteEnvelope = {
  data?: {
    is_success?: boolean;
    message?: string;
  };
};

const AddNewRoleForm: React.FC<AddNewRoleFormProps> = ({
  form,
  editData,
  onClose,
  fetchAllRoles,
}) => {
  const { showSpin, hideSpin } = useSpin();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { token } = useToken() as { token: string | null };
  const { unauthorize } = useUnAuthorize();
  const isActionNeeded = !!editData?.is_connected_with_anything;

  useEffect(() => {
    if (editData?.id) {
      form.setFieldsValue({ name: editData.name ?? '' });
    } else {
      form.resetFields();
    }
  }, [editData, form]);

  const onFinish = (values: AddNewRoleFormValues) => {
    showSpin();

    const model: { id?: string; name: string } = {
      name: values.name,
      ...(editData?.id ? { id: editData.id } : {}),
    };

    AddNewRole(token as string, model)
      .then((res) => {
        const payload = (res as { data?: ApiCreateUpdateEnvelope })?.data?.data;
        const ok = Boolean(payload?.is_success);
        const msg = payload?.message ?? (ok ? 'Saved' : 'Something went wrong');

        if (ok) {
          message.success(msg);
          onClose();
          fetchAllRoles();
        } else {
          message.error(msg);
        }
      })
      .catch((error: ErrorLike) => {
        unauthorize(error);
      })
      .finally(hideSpin);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    if (!editData?.id) return;

    showSpin();
    DeleteRole(editData.id, token as string)
      .then((res) => {
        const payload = (res as { data?: ApiDeleteEnvelope })?.data?.data;
        const ok = Boolean(payload?.is_success);
        const msg =
          payload?.message ?? (ok ? 'Removed' : 'Something went wrong');

        if (ok) {
          onClose();
          fetchAllRoles();
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .catch((error: ErrorLike) => {
        unauthorize(error);
      })
      .finally(hideSpin);
  };

  return (
    <>
      <Form<AddNewRoleFormValues>
        form={form}
        name='validateOnly'
        layout='vertical'
        onFinish={onFinish}
        autoComplete='off'
        className='drawer-children-h flex flex-col justify-between'
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div className='drawer-title-header'>
          <CustomTitle
            text={editData?.id ? 'Edit role' : 'Add role'}
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={0}
          />
        </div>

        <div className='add-role-scroll-body custom-scrollbar'>
          <Row gutter={[16, 14]}>
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Name'
                name='name'
                rules={[
                  { required: true, message: 'Please enter role name' },
                  {
                    type: 'string',
                    min: 3,
                    message: 'Role name must be at least 3 characters long',
                  },
                ]}
                style={{ marginBottom: '0px', width: '100%' }}
              >
                <CustomInput
                  placeholder='e.g., Partner, Driver'
                  bgColor='var(--light-gray)'
                  borderRadius='8px'
                  height='36px'
                  border='none'
                  lineHeight='20px'
                  color='var(--text-primary)'
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className='drawer-custom-footer'>
          <Row gutter={[0, 8]} style={{ marginTop: 0 }}>
            <CustomButton
              key='button1'
              text='Save'
              bgColor='#000'
              htmlType='submit'
              color='#fff'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
              borderRadius='8px'
              width='100%'
            />

            {editData?.is_delete && editData?.id && (
              <CustomButton
                key={`button-${editData.id}`}
                text='Remove'
                bgColor='var(--light-gray)'
                color='var(--text-primary)'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
                height='48px'
                borderRadius='8px'
                width='100%'
                border='none'
                onClick={handleDelete}
              />
            )}

            <CustomButton
              key='button3'
              text='Cancel'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
              width='100%'
              border='none'
              onClick={onClose}
            />
          </Row>
        </div>
      </Form>

      <CustomDeleteModal
        visible={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={() => setShowDeleteModal(false)}
        title={isActionNeeded ? 'Action needed' : 'Remove role'}
        message={
          isActionNeeded
            ? 'Roles with active users cannot be removed. First, change role of users before removing.'
            : 'All data assigned to this role will be removed.'
        }
        cancelText={isActionNeeded ? 'Close' : 'Cancel'}
        confirmText={isActionNeeded ? undefined : 'Remove'}
        confirmColor='var(--danger)'
      />
    </>
  );
};

export default AddNewRoleForm;
